import { Typography } from '@mui/material';
import { FC, MutableRefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Icon from 'src/units/Icon';
import {
  Container,
  ContentSection,
  Description,
  ScrollWrapper,
  Title,
  TooltipButton,
  TopSection,
  TopSectionActions,
} from './styles';
import { DeviceManager } from 'src/utils';
import { Anchor } from 'src/components/tag-tooltip/Components/Anchor/Anchor';
import { BaseTagTooltipProps } from 'src/components/tag-tooltip/types';
import truncate from 'src/components/tag-popup/utils';
import { TourActionType } from 'src/sdk-components/VSObject';
import { TagCategories, TagCategoriesTypes } from 'src/service/tag/enums';
import { convertMDLink } from 'src/utils/convertMDLink';
import { renderMediaContent } from './config';

export const BaseTagTooltip: FC<BaseTagTooltipProps & { children?: any }> = ({
  setTagPopupOpen,
  tag,
  camera,
  renderer,
  settings = { adminPanelAccess: false },
  handleClickDescriptionLink,
  translation = {},
  descriptionEnabled = true,
  tagNotesEnabled,
  onNoteClick,
  // pauseTourMusic,
  // playTourMusic
}) => {
  const [isClamped, setIsClamped] = useState(false);
  const [inFullScreen, setInFullScreen] = useState(false);
  const textRef: MutableRefObject<HTMLDivElement | undefined> = useRef<HTMLDivElement>();
  // const hasVideoUrl = (
  //   (tag?.showVideoUrl && tag?.videoUrl && tag?.videoUrl !== 'null') ||
  //   (Array.isArray(tag?.tagGalleries) && tag.tagGalleries.some(item =>
  //     typeof item?.url === 'string' && item.url.match(/\.(mp4|mov|avi|webm|mkv)$/i)
  //   ))
  // );

  const trimmedDescription = useMemo(() => {
    if (!tag?.description) return '';
    return convertMDLink(truncate(tag.description, 250));
  }, [tag]);

  const handleExpandButtonClick = () => {
    if (setTagPopupOpen) {
      // TEMP comment pauseTourMisic for hover until establishment
      // if (hasVideoUrl)  {
      //   pauseTourMusic()
      // };
      setTagPopupOpen(true, false);
    }
  };

  const handleGalleryItemClick = () => {
    if (setTagPopupOpen) setTagPopupOpen(true, false);
  };

  const handleReadMoreClick = () => {
    if (setTagPopupOpen) setTagPopupOpen(true, true);
  };

  const checkIfClamped = () => {
    const element = textRef.current;
    if (element) {
      const isOverflowing =
        element.scrollHeight > element.clientHeight || tag?.description?.length > 250;
      setIsClamped(isOverflowing);
    }
  };

  const toggleFullScreen = (ref) => {
    if (!document.fullscreenElement) {
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else {
      if (document.exitFullscreen) {
        setInFullScreen(false);
        document.exitFullscreen();
      }
    }
  };
  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    // TEMP comment pauseTourMisic for hover until establishment
    // if(hasVideoUrl) pauseTourMusic();
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      // TEMP comment playTourMusic for hover until establishment
      // if(hasVideoUrl) playTourMusic();
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  useLayoutEffect(() => {
    checkIfClamped();
  }, [tag]);

  const excludedTypes = [
    ...TagCategoriesTypes[TagCategories.MoveTo],
    TourActionType.ExperiencePopUp,
    TourActionType.Music,
  ];

  function handleClick(event) {
    const target = event.target.closest('a');
    if (target) {
      event.preventDefault();
      if (handleClickDescriptionLink) handleClickDescriptionLink(target.href);
    }
  }

  // @ts-ignore
  return (
    <div id="base-tag-tooltip">
      <Anchor tag={tag} camera={camera} renderer={renderer} settings={settings}>
        <Container>
          <TopSection>
            <Title>{tag?.mpTitle && tag?.title}</Title>
            <TopSectionActions>
              {tagNotesEnabled && (
                <TooltipButton
                  onClick={() => {
                    onNoteClick?.();
                  }}
                >
                  <Icon.Chat />
                </TooltipButton>
              )}

              {tag.expandContentBtn && (
                <TooltipButton
                  style={{ visibility: excludedTypes.includes(tag.type) ? 'hidden' : 'visible' }}
                  onClick={handleExpandButtonClick}
                >
                  <Icon.Expand style={{ width: '26px', height: '26px' }} />
                </TooltipButton>
              )}
            </TopSectionActions>
          </TopSection>
          <ScrollWrapper style={{ maxHeight: DeviceManager.isMobile() ? '360px' : '500px' }}>
            {descriptionEnabled && tag?.description ? (
              <ContentSection>
                {/*@ts-ignore*/}
                <Description
                  //@ts-ignore\
                  ref={textRef}
                  dangerouslySetInnerHTML={{ __html: trimmedDescription }}
                  onClick={handleClick}
                />
                {isClamped ? (
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    onClick={handleReadMoreClick}
                  >
                    {` ${translation?.readMore || 'Read more'}`}
                  </Typography>
                ) : null}
              </ContentSection>
            ) : null}
            {tag?.tagGalleries?.length ||
            (tag?.showVideoUrl && tag?.videoUrl && tag?.videoUrl !== 'null') ? (
              <ContentSection>
                {renderMediaContent(
                  tag,
                  handleGalleryItemClick,
                  settings,
                  inFullScreen,
                  toggleFullScreen,
                )}
              </ContentSection>
            ) : null}
          </ScrollWrapper>
        </Container>
      </Anchor>
    </div>
  );
};
